import {
  LayoutDashboard,
  Package,
  Warehouse,
  ArrowRightLeft,
  ClipboardCheck,
  Barcode,
  ShoppingCart,
  Truck,
  Users,
  TrendingUp,
  Package2,
  FolderTree,
  Factory,
  FileText,
  Wrench,
  BrainCircuit,
  Target,
  TrendingDown,
  PoundSterling,
  Grid3x3,
  BarChart3,
  DollarSign,
  Settings,
  Home,
  PackageOpen,
  Sparkles,
  Zap,
  PackageMinus,
  FolderKanban,
} from "lucide-react";
import { MenuItem } from "../types";
import { getCurrentFeatureFlags } from "@/config/feature-flags";

/**
 * Phase 1 Badge Configuration
 * Identifies which menu items are part of Phase 1 release
 */
export type PhaseStatus = "phase1" | "phase2" | "future";

export interface PhaseInfo {
  status: PhaseStatus;
  label?: string;
  tooltip?: string;
}

/**
 * Options for customizing menu items
 */
export interface MenuItemsOptions {
  /** Custom label for level 1 hierarchy (replaces "Locations" in Inventory and Settings) */
  level1Label?: string;
}

/**
 * Get menu items with feature flag filtering applied
 * This function dynamically filters menu items based on enabled features
 */
export const getMenuItems = (options: MenuItemsOptions = {}): MenuItem[] => {
  console.log("🔄 Loading menu items - NEW STRUCTURE - TIMESTAMP:", Date.now());
  const flags = getCurrentFeatureFlags();
  const { level1Label = "Locations" } = options;

  const baseMenuItems: MenuItem[] = [
  // Top Level Pages (no category)
  { id: "dashboard", label: "Home", icon: Home, page: "dashboard", phase: "phase1" as PhaseStatus },
  { id: "goods-in-out", label: "Goods In / Out", icon: PackageOpen, page: "goods-in-out" },
  { id: "stock-assistant", label: "Stock Assistant", icon: Sparkles, page: "stock-assistant" },
  { id: "quick-start", label: "Quick Start", icon: Zap, page: "quick-start" },
  // Inventory Section
  {
    id: "inventory",
    label: "Inventory",
    icon: Package,
    page: "inventory",
    phase: "phase1" as PhaseStatus,
    children: [
      { id: "stock-items", label: "Stock Items", icon: Package, page: "stock-items", phase: "phase1" as PhaseStatus },
      { id: "non-stock-items", label: "Non-Stock Items", icon: PackageMinus, page: "non-stock-items" },
      ...(flags.enableWarehouseManagement ? [{ id: "stock-transfers", label: "Stock Transfers", icon: ArrowRightLeft, page: "stock-transfers", phase: "phase1" as PhaseStatus }] : []),
      { id: "stock-movements", label: "Stock Movements", icon: ArrowRightLeft, page: "stock-movements", phase: "phase1" as PhaseStatus },
      { id: "tracking", label: "Lot/Batch/Serial Tracking", icon: Barcode, page: "tracking" },
    ],
  },
  // Warehouse Operations Section
  {
    id: "warehouse-operations",
    label: "Warehouse Operations",
    icon: Warehouse,
    page: "warehouse-operations",
    phase: "phase1" as PhaseStatus,
    children: [
      { id: "receive-goods", label: "Receive Goods", icon: Package2, page: "receive-goods", phase: "phase1" as PhaseStatus },
      ...(flags.enableWarehouseManagement ? [{ id: "warehouses", label: level1Label, icon: Warehouse, page: "warehouses", phase: "phase1" as PhaseStatus }] : []),
      { id: "stock-takes", label: "Stock Takes", icon: ClipboardCheck, page: "stock-takes", phase: "phase1" as PhaseStatus },
    ],
  },
  // Purchasing Section
  {
    id: "purchasing",
    label: "Purchasing",
    icon: ShoppingCart,
    page: "purchasing",
    phase: "phase1" as PhaseStatus,
    children: [
      { id: "purchase-orders", label: "Purchase Orders", icon: ShoppingCart, page: "purchase-orders", phase: "phase1" as PhaseStatus },
      { id: "suppliers", label: "Suppliers", icon: Truck, page: "suppliers", phase: "phase1" as PhaseStatus },
    ],
  },
  // Sales Section
  {
    id: "sales",
    label: "Sales",
    icon: TrendingUp,
    page: "sales",
    phase: "phase1" as PhaseStatus,
    children: [
      { id: "sales-orders", label: "Sales Orders", icon: TrendingUp, page: "sales-orders", phase: "phase1" as PhaseStatus },
      { id: "customers", label: "Customers", icon: Users, page: "customers", phase: "phase1" as PhaseStatus },
    ],
  },
  // Manufacturing Section
  {
    id: "manufacturing",
    label: "Manufacturing",
    icon: Factory,
    page: "manufacturing",
    phase: "future" as PhaseStatus,
    children: [
      { id: "boms", label: "Bills of Material (BOMs)", icon: FileText, page: "boms", phase: "future" as PhaseStatus },
      { id: "work-orders", label: "Work Orders", icon: Wrench, page: "work-orders", phase: "future" as PhaseStatus },
      // Commented out - hidden but preserved
      // { id: "production-schedule", label: "Production Schedule", icon: BarChart3, page: "production-schedule", phase: "future" as PhaseStatus },
      // { id: "routing", label: "Routings & Work Centers", icon: Factory, page: "routing", phase: "future" as PhaseStatus },
    ],
  },
  // Projects & Cost Centers (Top Level)
  { id: "projects-cost-centers", label: "Projects & Cost Centers", icon: FolderKanban, page: "projects-cost-centers" },
  // Analytics & Forecasting Section
  {
    id: "analytics",
    label: "Analytics & Forecasting",
    icon: BrainCircuit,
    page: "analytics",
    children: [
      { id: "ai-forecasting", label: "Demand Forecasting", icon: TrendingUp, page: "ai-forecasting" },
      { id: "abc-classification", label: "ABC Classification", icon: Target, page: "abc-classification" },
      { id: "reorder", label: "Reorder Recommendations", icon: TrendingDown, page: "reorder", phase: "future" as PhaseStatus },
      { id: "margin-analysis", label: "Margin Analysis", icon: BarChart3, page: "margin-analysis", phase: "future" as PhaseStatus },
      { id: "cost-analysis", label: "Cost Analysis", icon: DollarSign, page: "cost-analysis", phase: "future" as PhaseStatus },
    ],
  },
  // Pricing Section
  {
    id: "pricing",
    label: "Pricing",
    icon: PoundSterling,
    page: "pricing",
    phase: "future" as PhaseStatus,
    children: [
      { id: "price-matrix", label: "Price Matrix", icon: Grid3x3, page: "price-matrix", phase: "future" as PhaseStatus },
      { id: "advanced-pricing", label: "Advanced Pricing", icon: DollarSign, page: "advanced-pricing" },
    ],
  },
  // Settings Section
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    page: "settings",
    phase: "phase1" as PhaseStatus,
    children: [
      { id: "stock-options", label: "System Configuration", icon: Settings, page: "stock-options", phase: "phase1" as PhaseStatus },
      { id: "warehouse-location-setup", label: level1Label, icon: Warehouse, page: "warehouse-location-setup", phase: "phase1" as PhaseStatus },
      { id: "integration-sync", label: "Integration", icon: ArrowRightLeft, page: "integration-sync", phase: "phase1" as PhaseStatus },
      { id: "company-settings", label: "Company Settings", icon: FileText, page: "company-settings", phase: "phase1" as PhaseStatus },
      { id: "user-management", label: "User Management", icon: Users, page: "user-management", phase: "phase1" as PhaseStatus },
      { id: "custom-fields", label: "Custom Fields", icon: Grid3x3, page: "custom-fields", phase: "phase1" as PhaseStatus },
      { id: "fifo-demo", label: "FIFO Demo", icon: Package, page: "fifo-demo" },
      { id: "permissions-demo", label: "Permissions Demo", icon: Users, page: "permissions-demo" },
      { id: "help-system-demo", label: "Help System Demo", icon: FileText, page: "help-system-demo" },
    ],
  },
  ];

  console.log("✅ Returning menu items:", baseMenuItems.map(m => m.id));

  return baseMenuItems;
};

/**
 * Backward compatible export - gets menu items with feature flags applied
 */
export const menuItems = getMenuItems();

/**
 * Company Options Configuration (Database/Environment Selector)
 */
export const companies = [
  { value: "new-company", label: "New Company" },
  { value: "production", label: "Acme Corporation Ltd" },
  { value: "staging", label: "Beta Industries Ltd" },
  { value: "test", label: "Gamma Trading Co" },
];

/**
 * Get menu configuration with dynamic page count
 */
export const getMenuConfig = () => {
  const items = getMenuItems();
  return {
    /** Total number of pages - calculated from menuItems */
    totalPages: items.reduce((total, item) => {
      return total + 1 + (item.children?.length || 0);
    }, 0),
    /** Minimum pages required to enable favorites feature */
    favoritesThreshold: 10,
    /** Show company switcher in footer */
    showDatabaseSwitcher: true,
    /** Show settings button in footer - settings is already in main menu */
    showSettings: false,
  };
};

/**
 * Menu Configuration - backward compatible export
 */
export const menuConfig = getMenuConfig();
